import { alpha, Box, Chip, Grid, IconButton, makeStyles, Table, TableBody, TableCell, TableHead, TableRow, Theme, Typography } from "@material-ui/core";
import { Block, CheckCircle, Edit } from "@material-ui/icons";
import { Paper, PaperBorderType, CurrencyField, StandardDialog, Button, TextField } from '@roc/ui';
import { PayoffReconciliationReviewStore } from "@roc/feature-payoffs";
import { observer } from "mobx-react";
import { formatCurrency } from "@roc/ui/utils";

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    minWidth: 750,
    border: '2px #e0e0e0 solid',
    '& td': {
      height: '40px',
      padding: 0,
      paddingTop: '8px',
      paddingBottom: '8px',
      paddingLeft: theme.spacing(2),
      paddingRight: '3px',
    },
    '& th:first-child, td[rowSpan], $addNewItemRow td': {
      paddingLeft: theme.spacing(2),
      paddingRight: '3px',
    },
    '& table tr:last-child td, & table td[rowSpan]': {
      borderBottom: 'none',
    },
  },
  headerCell: {
    height: '41px',
    fontWeight: 'bold',
    padding: '0 4px 0 0',
    paddingLeft: theme.spacing(2),
    paddingRight: '3px',
  },
  titleCell: {
    height: '46px',
    padding: 0,
    borderBottom: '2px #e0e0e0 solid',
    width: '100%',
    display: 'table-cell',
    boxSizing: 'border-box',
  },
  title: {
    color: '#333333',
    fontSize: 20,
    fontWeight: 'bold',
  },
  border: {
    paddingLeft: theme.spacing(2),
    paddingRight: '3px',
  },
}));

interface Props {
  store: PayoffReconciliationReviewStore;
  allowPayoffWaiverReview: boolean;
  categoryTitle: string;
  categoryContents: any[];
  setIsApproveDialogOpen: (boolean: boolean) => void;
  setIsRejectDialogOpen: (boolean: boolean) => void;
  handleOpenModal: (item: any) => void;
  setRecord: (item: any) => void;
}

export const PayoffReconciliationReviewCategory = observer((props: Props) => {
  const { store, allowPayoffWaiverReview, categoryTitle, categoryContents,
    setIsApproveDialogOpen, setIsRejectDialogOpen, handleOpenModal, setRecord,
   } = props;
  const classes = useStyles();

  const closingManagerColumns = [
    { name: 'Status', width: '10%', },
    { name: 'Fee Type',  width: '10%', },
    { name: 'Current Amount', width: '10%', },
    { name: 'Requested to Waive', width: '10%', },
    { name: 'Comments', width: '45%', },
    { name: 'Edit', width: '5%', },
    { name: 'Approve', width: '5%', },
    { name: 'Reject', width: '5%', },
  ];
  const closingSpecialistColumns = [
    { name: 'Status', width: '10%', },
    { name: 'Fee Type', width: '10%', },
    { name: 'Current Amount', width: '10%', },
    { name: 'Requested to Waive', width: '10%', },
    { name: 'Comments', width: '53%', },
    { name: 'Edit', width: '7%', },
  ];

  const columnStyles = allowPayoffWaiverReview ? closingManagerColumns : closingSpecialistColumns;

  const getRowColor = (row) => {
    if (row.id === null) {
      return 'lightyellow'
    }
  }

  const getStatusChip = (status: string) => {
    const backgroundColor = status === 'Rejected'
      ? '#d6828a'
      : status === 'Pending'
        ? '#F5B945'
        : '#89C053';
    return (
      <Chip
        size="small"
        label={status}
        style={{
          backgroundColor: backgroundColor,
          color: 'white',
        }}
      />
    )
  }

  return (
    <Grid item xs={12} md={12}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell colSpan={columnStyles.length} className={classes.titleCell}>
              <Typography className={classes.title}>{categoryTitle}</Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            {columnStyles.map(col => (
              <TableCell key={col.name} className={classes.headerCell}>{col.name}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {categoryContents.map((item, idx) => (
            <TableRow key={idx} className={getRowColor(item)}>
              <TableCell className={classes.border} width={columnStyles[0].width}>{getStatusChip(item.status)}</TableCell>
              <TableCell className={classes.border} width={columnStyles[1].width}>{item.name}</TableCell>
              <TableCell className={classes.border} width={columnStyles[2].width}>{formatCurrency(item.originalAmount ? item.originalAmount : 0)}</TableCell>
              <TableCell className={classes.border} width={columnStyles[3].width}>{formatCurrency(item.requestedAmount ? item.requestedAmount : 0)}</TableCell>
              <TableCell className={classes.border} width={columnStyles[4].width}>
                <Typography style={{ whiteSpace: 'pre-line' }}>
                  {item.comments}
                </Typography>
              </TableCell>
              <TableCell className={classes.border} width={columnStyles[5].width}>
                {!store.isRequestResolved && (
                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent="space-between"
                    px={0}
                    py={1}
                    ml={0}
                  >
                    <IconButton
                      aria-label="edit"
                      size="small"
                        onClick={async () => {
                          handleOpenModal(item);
                        }}
                    >
                      <Edit />
                    </IconButton>
                  </Box>
                )}
              </TableCell>
              {allowPayoffWaiverReview && (
                <>
                  <TableCell className={classes.border} width={columnStyles[6].width}>
                    {!store.isRequestResolved && (
                      <Button
                        testId='approveRequest'
                        variant='contained'
                        size='small'
                        onClick={() => {
                          setIsApproveDialogOpen(true);
                          setRecord(item);
                        }}
                        style={{ backgroundColor: 'white' }}
                      >
                        <CheckCircle style={{ color: '#89C053' }} />
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className={classes.border} width={columnStyles[7].width}>
                    {!store.isRequestResolved && (
                      <Button
                        testId='rejectRequest'
                        variant='contained'
                        size='small'
                        onClick={() => {
                          setIsRejectDialogOpen(true);
                          setRecord(item);
                        }}
                        style={{ backgroundColor: 'white' }}
                      >
                        <Block style={{ color: '#d6828a' }} />
                      </Button>
                    )}
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Grid>
  );
});
